import { useState, useEffect, useCallback, useRef } from 'react';

/**
 * Custom hook for Checkout.com Apple Pay via CKO Flow / Web Components.
 *
 * Loads the Checkout.com Web Components SDK, initialises CheckoutWebComponents
 * with a backend-created payment session, creates the `applepay` component,
 * and exposes mount/unmount helpers so callers can render the CKO-rendered
 * Apple Pay button into any DOM target (typically a slot exposed by
 * OrderSummary so the "Pay and Subscribe" button is replaced Stripe-style).
 *
 * NOTE: there is some overlap with useCheckoutCard — both load the same SDK
 * script and instantiate CheckoutWebComponents. We keep them separate for
 * now to avoid coupling; they can be folded into a single generic
 * `useCheckoutWebComponents(componentType)` hook once both use identical
 * lifecycles (tracked in the scalability refactor plan).
 *
 * @param {Object} config
 * @param {string} config.publicKey - Checkout.com public key
 * @param {Object} config.paymentSession - Full response from
 *        POST /api/checkout/v1/{sessionId}/checkout-com/payment-session
 * @param {string} config.environment - 'sandbox' | 'production'
 * @param {Object} config.appearance - Appearance object accepted by CheckoutWebComponents
 * @param {Object} config.componentOptions - Global CheckoutWebComponents componentOptions
 * @param {Object} config.applePayOptions - Options forwarded to checkout.create('applepay', ...)
 * @param {Function} config.onReady
 * @param {Function} config.onPaymentCompleted - (component, paymentResponse) => void
 * @param {Function} config.onError - (errorMessage, rawError) => void
 *
 * @returns {Object} { applePayComponent, ready, loading, error, isAvailable,
 *                     mount, unmount, clearError, checkoutInstance }
 */
export const useCheckoutApplePay = ({
  publicKey,
  paymentSession,
  environment = 'sandbox',
  appearance = {},
  componentOptions = {},
  applePayOptions = {},
  onReady,
  onPaymentCompleted,
  onError,
}) => {
  const [checkoutInstance, setCheckoutInstance] = useState(null);
  const [applePayComponent, setApplePayComponent] = useState(null);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isAvailable, setIsAvailable] = useState(false);

  const componentRef = useRef(null);
  const checkoutRef = useRef(null);
  const mountedRef = useRef(false);

  const onReadyRef = useRef(onReady);
  const onPaymentCompletedRef = useRef(onPaymentCompleted);
  const onErrorRef = useRef(onError);

  useEffect(() => {
    onReadyRef.current = onReady;
    onPaymentCompletedRef.current = onPaymentCompleted;
    onErrorRef.current = onError;
  }, [onReady, onPaymentCompleted, onError]);

  // Load the CKO Web Components SDK (idempotent — uses the same script id as useCheckoutCard).
  const loadScript = () =>
    new Promise((resolve, reject) => {
      if (window.CheckoutWebComponents && typeof window.CheckoutWebComponents === 'function') {
        resolve(window.CheckoutWebComponents);
        return;
      }

      const existing = document.getElementById('checkout-web-components-sdk');
      const finish = () => {
        let attempts = 0;
        const check = () => {
          attempts += 1;
          if (window.CheckoutWebComponents && typeof window.CheckoutWebComponents === 'function') {
            resolve(window.CheckoutWebComponents);
          } else if (attempts >= 30) {
            reject(new Error('Checkout.com SDK failed to initialize'));
          } else {
            setTimeout(check, 50);
          }
        };
        setTimeout(check, 50);
      };

      if (existing) {
        finish();
        return;
      }

      const script = document.createElement('script');
      script.src = 'https://checkout-web-components.checkout.com/index.js';
      script.async = false;
      script.id = 'checkout-web-components-sdk';
      script.onload = finish;
      script.onerror = () => reject(new Error('Failed to load Checkout.com SDK'));
      document.head.appendChild(script);
    });

  const initializeApplePay = useCallback(async () => {
    if (!window.CheckoutWebComponents) return;

    if (componentRef.current) {
      try {
        if (mountedRef.current) componentRef.current.unmount();
      } catch (_e) {
        /* ignore */
      }
      componentRef.current = null;
      setApplePayComponent(null);
      setReady(false);
      mountedRef.current = false;
    }

    try {
      const checkout = await window.CheckoutWebComponents({
        paymentSession,
        publicKey,
        environment,
        appearance,
        componentOptions,
      });
      checkoutRef.current = checkout;
      setCheckoutInstance(checkout);

      const applepay = checkout.create('applepay', {
        ...applePayOptions,
        onReady: (component) => {
          setReady(true);
          onReadyRef.current?.(component);
        },
        onPaymentCompleted: (component, paymentResponse) => {
          onPaymentCompletedRef.current?.(component, paymentResponse);
        },
        onError: (component, err) => {
          const message = err?.message || 'Apple Pay error';
          setError(message);
          onErrorRef.current?.(message, err);
        },
      });

      try {
        const available = await applepay.isAvailable();
        setIsAvailable(!!available);
      } catch (_e) {
        setIsAvailable(false);
      }

      componentRef.current = applepay;
      setApplePayComponent(applepay);
    } catch (err) {
      const message = err?.message || 'Failed to initialize Apple Pay';
      setError(message);
      onErrorRef.current?.(message, err);
    }
  }, [publicKey, paymentSession, environment, appearance, componentOptions, applePayOptions]);

  useEffect(() => {
    if (!publicKey || !paymentSession) return undefined;

    let cancelled = false;
    setLoading(true);
    setError(null);

    loadScript()
      .then(() => {
        if (!cancelled) return initializeApplePay();
        return undefined;
      })
      .catch((err) => {
        if (cancelled) return;
        const message = err?.message || 'Failed to load Apple Pay';
        setError(message);
        onErrorRef.current?.(message, err);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [publicKey, paymentSession, environment, initializeApplePay]);

  const mount = useCallback(async (target) => {
    if (!componentRef.current) {
      return;
    }
    if (mountedRef.current) {
      return;
    }

    try {
      const available = await componentRef.current.isAvailable();
      if (!available) {
        setIsAvailable(false);
        return;
      }

      let targetElement;
      if (typeof target === 'string') {
        targetElement = document.querySelector(target);
        if (!targetElement) {
          throw new Error(`Apple Pay mount target not found: ${target}`);
        }
      } else if (target instanceof HTMLElement) {
        targetElement = target;
      } else {
        throw new Error('Invalid Apple Pay mount target: must be selector or HTMLElement');
      }

      componentRef.current.mount(targetElement);
      mountedRef.current = true;
    } catch (err) {
      const message = err?.message || 'Failed to mount Apple Pay';
      setError(message);
      onErrorRef.current?.(message, err);
    }
  }, []);

  const unmount = useCallback(() => {
    if (!componentRef.current) return;

    try {
      componentRef.current.unmount();
    } catch (_e) {
      /* ignore */
    }
    componentRef.current = null;
    setApplePayComponent(null);
    setReady(false);
    mountedRef.current = false;
  }, []);

  const clearError = useCallback(() => setError(null), []);

  return {
    checkoutInstance,
    applePayComponent,
    ready,
    loading,
    error,
    isAvailable,
    mount,
    unmount,
    clearError,
  };
};

export default useCheckoutApplePay;
